import { useCallback, useEffect, useRef, useState } from "react";
import Icon from "../panel/Icon";
import { uiTexts } from "../../i18n.js";

const reducedMotion = () => typeof window !== "undefined" && !!window.matchMedia
    && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Fila de diapositivas que se pasan con el dedo (scroll-snap), con las flechas o con los
// puntos. La usan la galería de fotos del producto y el banner de promociones.
// `auto` (en ms) la hace avanzar sola; se frena mientras el cliente la toca o tiene el
// mouse encima, y no avanza sola si el teléfono pide menos movimiento.
const Carousel = ({ count, children, label, auto = 0, className = "", lang = "es" }) => {

    const t = uiTexts(lang);
    const track = useRef(null);
    const [index, setIndex] = useState(0);
    const [paused, setPaused] = useState(false);
    const resume = useRef(null);

    const goTo = useCallback((i, smooth = true) => {
        const el = track.current;
        if (!el || !count) return;
        const next = (i + count) % count;
        el.scrollTo({ left: next * el.clientWidth, behavior: smooth && !reducedMotion() ? "smooth" : "auto" });
        setIndex(next);
    }, [count]);

    // El punto activo sigue a lo que se ve, también cuando se pasa con el dedo.
    const onScroll = () => {
        const el = track.current;
        if (!el || !el.clientWidth) return;
        const i = Math.round(el.scrollLeft / el.clientWidth);
        if (i !== index && i >= 0 && i < count) setIndex(i);
    };

    useEffect(() => {
        if (!auto || count < 2 || paused || reducedMotion()) return;
        const id = setTimeout(() => goTo(index + 1), auto);
        return () => clearTimeout(id);
    }, [auto, count, paused, index, goTo]);

    // Al tocarla se frena un rato y después sigue sola.
    const hold = () => {
        setPaused(true);
        clearTimeout(resume.current);
    };
    const release = (delay = 0) => {
        clearTimeout(resume.current);
        resume.current = setTimeout(() => setPaused(false), delay);
    };
    useEffect(() => () => clearTimeout(resume.current), []);

    // Si cambia la cantidad (por ejemplo, una promo que vence), vuelve al principio.
    useEffect(() => {
        if (index >= count) goTo(0, false);
    }, [count, index, goTo]);

    const many = count > 1;

  return (
    <div className={"lp-menu-carousel " + className} role="region" aria-roledescription="carrusel" aria-label={label}
        onMouseEnter={hold} onMouseLeave={() => release()}
        onTouchStart={hold} onTouchEnd={() => release(6000)}
        onFocus={hold} onBlur={() => release()}
        onKeyDown={(e) => {
            if (e.key === "ArrowRight") goTo(index + 1);
            if (e.key === "ArrowLeft") goTo(index - 1);
        }}>
        <div className="lp-menu-carousel-track" ref={track} onScroll={onScroll}>
            {children.map((child, i) => (
                <div key={i} className="lp-menu-carousel-slide" role="group" aria-roledescription="diapositiva" aria-label={t.slideOf(i + 1, count)}>
                    {child}
                </div>
            ))}
        </div>
        {many &&
            <>
                <button type="button" className="lp-menu-carousel-arrow lp-menu-carousel-prev" aria-label={t.prevSlide} onClick={() => goTo(index - 1)}><Icon name="arrow" size={18} strokeWidth={2.4}></Icon></button>
                <button type="button" className="lp-menu-carousel-arrow lp-menu-carousel-next" aria-label={t.nextSlide} onClick={() => goTo(index + 1)}><Icon name="arrow" size={18} strokeWidth={2.4}></Icon></button>
                <div className="lp-menu-carousel-dots">
                    {children.map((_, i) => (
                        <button key={i} type="button" className={i === index ? "lp-menu-carousel-dot lp-menu-carousel-dot-on" : "lp-menu-carousel-dot"} aria-label={t.slideOf(i + 1, count)} aria-current={i === index} onClick={() => goTo(i)}></button>
                    ))}
                </div>
            </>
        }
    </div>
  )
}

export default Carousel
